import React, { useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { BUTTON_THEMES, ICON_TYPES } from '../../../shared/constants';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_COMPS, DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink } from '../../../utils/generic';
import ReactMarkdownExtended from '../../../utils/generic/ReactMarkdownExtended';
import { RichTextHtml } from '../../../utils/richtext/richTextRender';

import mq from '../../../styles/breakpoints';
import buttonStyles from '../../../styles/buttonStyles';
import Colors from '../../../styles/colors';
import { Body, H2 } from '../../../styles/generic';

import check from '../../../images/check.svg';
import Modal from '../../organisms/modal/Modal';
import { SimpleContent } from '../simple-content/SimpleContent';

const HeaderCtaCard = ({
  ctaLinkPrimary,
  ctaLinkSecondary,
  ctaText,
  key,
  image,
  uspBody,
  subtitle,
  title,
  body,
  productName,
}) => {
  const [showModal, setShowModalHandler] = useState(false);
  const [activeCtaLink, setActiveCtaLink] = useState();

  const handleOpenModal = (link) => {
    setActiveCtaLink(link);
    setShowModalHandler(true);
  };

  return (
    <>
      <Container data-testid="product-header-cta-card">
        <Component key={key}>
          <TopPartCtaCard>
            {image?.gatsbyImageData ? (
              <ImageContainer>
                <GatsbyImage alt="" image={image.gatsbyImageData} />
              </ImageContainer>
            ) : (
              image?.file && (
                <ImageContainer>
                  <CardImage src={image.file.url} />
                </ImageContainer>
              )
            )}
            {title && <Title>{title}</Title>}
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
            {ctaLinkPrimary && (
              <StyledPrimaryButton
                showModal={ctaLinkPrimary.dialogueModal}
                onButtonClick={() =>
                  ctaLinkPrimary.dialogueModal
                    ? handleOpenModal(ctaLinkPrimary)
                    : undefined
                }
                key={`prim-btn-${ctaLinkPrimary.label}`}
                type={ctaLinkPrimary.type}
                label={ctaText || ctaLinkPrimary.label || ctaLinkPrimary.name}
                to={getCtaLink(ctaLinkPrimary)}
                className="primary-button"
                iconPosition={ctaLinkPrimary.iconPosition}
                icon={ctaLinkPrimary.icon}
                deqComponentTitle={title}
                deqCustomAttributes={{
                  componentType: 'product-ticket',
                  type: ctaLinkPrimary.dialogueModal
                    ? DEQ_TYPES.button
                    : DEQ_TYPES.foldout,
                  elementTarget: getCtaLink(ctaLinkPrimary),
                  elementStyle: 'primary',
                }}
                buttonTheme={BUTTON_THEMES.highlight}
              />
            )}
            {ctaLinkSecondary && (
              <StyledSecondaryButton
                onButtonClick={() =>
                  ctaLinkSecondary.dialogueModal &&
                  handleOpenModal(ctaLinkSecondary)
                }
                deqCustomLabel={ctaLinkSecondary.label}
                deqCustomType="product-ticket"
                type={ctaLinkSecondary.type}
                icon={
                  ctaLinkSecondary.dialogueModal
                    ? ICON_TYPES.info
                    : ctaLinkSecondary.icon
                }
                buttonTheme={BUTTON_THEMES.highlight}
                label={ctaLinkSecondary.label}
                iconPosition={
                  ctaLinkSecondary.dialogueModal
                    ? 'start'
                    : ctaLinkSecondary.position
                }
                showModal={Boolean(ctaLinkSecondary.dialogueModal)}
                to={getCtaLink(ctaLinkSecondary)}
                deqCustomAttributes={{
                  elementType: DEQ_TYPES.textLink,
                }}
              />
            )}
          </TopPartCtaCard>
          {body && (
            <BottomPartCtaCard>
              <Description>
                <ReactMarkdownExtended componentType={DEQ_COMPS.headerCtaCard}>
                  {body.childMarkdownRemark.rawMarkdownBody}
                </ReactMarkdownExtended>
              </Description>
            </BottomPartCtaCard>
          )}
          {uspBody?.raw && (
            <BottomPartCtaCard>
              <Description>
                <RichTextHtml
                  richTextNode={uspBody}
                  deqCompType={DEQ_COMPS.headerCtaCard}
                />
              </Description>
            </BottomPartCtaCard>
          )}
        </Component>
      </Container>
      <Modal
        show={showModal}
        onRequestCloseHandler={() => setShowModalHandler(!showModal)}
      >
        {activeCtaLink?.dialogueModal && (
          <SimpleContent
            setShowModal={(event) => {
              setShowModalHandler(event);
            }}
            title={activeCtaLink.dialogueModal.title}
            buttonLabel="Close"
            primaryLink={activeCtaLink?.dialogueModal.primaryLink}
          >
            <ReactMarkdownExtended componentType={DEQ_COMPS.headerCtaCard}>
              {
                activeCtaLink.dialogueModal.body.childMarkdownRemark
                  .rawMarkdownBody
              }
            </ReactMarkdownExtended>
          </SimpleContent>
        )}
      </Modal>
    </>
  );
};

export default HeaderCtaCard;

const Container = styled.div`
  display: flex;
  flex-direction: column;

  ${mq.from.breakpoint.M`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  `}
`;

const ImageContainer = styled.div`
  height: 110px;
  margin-bottom: 8px;
  width: 110px;

  ${mq.from.breakpoint.L`
    margin-bottom: 16px;
  `}
`;

const CardImage = styled.img`
  width: 100%;
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  padding-top: 24px;
`;

const TopPartCtaCard = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.headerCtaCard.topCard.background};
  display: flex;
  flex-direction: column;
  padding: 16px 16px 24px;
  position: relative;
  text-align: center;

  ${mq.from.breakpoint.M`
    padding: 40px;
  `}
`;

const BottomPartCtaCard = styled.div`
  padding: 16px;

  ${mq.from.breakpoint.M`
    padding: 40px;
  `}
`;

const Component = styled.div`
  background: ${Colors.white};
  box-shadow: ${({ theme }) => theme.elevations.abnElevationProduct};
  box-sizing: border-box;
  width: 100%;
`;

const Title = styled(H2)`
  color: ${Colors.white};
  margin-top: 0;
  text-align: center;
  width: 100%;
`;

const Subtitle = styled(Body)`
  color: ${Colors.white};
  margin-bottom: 32px;
  text-align: center;
  width: 100%;
`;

const Description = styled(Body)`
  ul {
    list-style-image: url(${check});
    padding-inline-start: 24px;
    margin-top: 0;
    margin-bottom: 0;
  }

  p {
    margin: 0;
  }

  li {
    margin-bottom: 8px;

    &:last-of-type {
      margin-bottom: 0;
    }
  }
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  -webkit-box-pack: center;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  text-align: center;
  width: 100%;
  ${buttonStyles.primaryButtonPosition}
`;
